import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import * as Dialog from '@radix-ui/react-dialog'
import { useQuery } from '@tanstack/react-query'
import { Search, ChevronRight, Plus } from 'lucide-react'
import { workspaceApi } from '@/lib/api'
import { usePaletteStore } from '@/stores/palette-store'
import { QuickTaskForm } from './quick-task-form'

export function CommandPalette() {
  const navigate = useNavigate()
  const open = usePaletteStore((s) => s.open)
  const setOpen = usePaletteStore((s) => s.setOpen)

  const [query, setQuery] = useState('')
  const [showTaskForm, setShowTaskForm] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // Reset state on open/close
  useEffect(() => {
    if (open) {
      setQuery('')
      setShowTaskForm(false)
    }
  }, [open])

  // Focus input when palette opens (slight delay for Dialog animation)
  useEffect(() => {
    if (!open) return
    const t = setTimeout(() => inputRef.current?.focus(), 50)
    return () => clearTimeout(t)
  }, [open])

  const { data: workspaces } = useQuery({
    queryKey: ['workspaces'],
    queryFn: workspaceApi.getWorkspaces,
    enabled: open,
  })

  const filtered =
    workspaces?.filter((ws) =>
      ws.name.toLowerCase().includes(query.toLowerCase())
    ) ?? []

  function handleWorkspaceClick(slug: string) {
    navigate(`/workspaces/${slug}/projects`)
    setOpen(false)
  }

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content
          aria-describedby={undefined}
          className="fixed left-1/2 top-[15%] z-50 w-full max-w-md -translate-x-1/2 rounded-xl border border-border bg-card data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=open]:slide-in-from-left-1/2"
        >
          <Dialog.Title className="sr-only">Command Palette</Dialog.Title>

          {/* Search input */}
          <div className="flex items-center gap-2 border-b border-border px-4 py-3">
            <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value)
                setShowTaskForm(false)
              }}
              placeholder="Cari workspace atau buat tugas..."
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
            <kbd className="shrink-0 rounded border border-border bg-muted px-1.5 py-0.5 text-xs font-mono text-muted-foreground">
              Esc
            </kbd>
          </div>

          {/* Results area */}
          <div className="max-h-[28rem] overflow-y-auto p-2">
            {!showTaskForm && (
              <>
                {/* Create task row — always visible at the top */}
                <button
                  onClick={() => setShowTaskForm(true)}
                  className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-left transition-colors hover:bg-secondary"
                >
                  <Plus className="h-4 w-4 shrink-0 text-primary" />
                  <span className="flex-1">
                    Buat tugas
                    {query.trim() && (
                      <>
                        :{' '}
                        <span className="font-medium">"{query.trim()}"</span>
                      </>
                    )}
                  </span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                </button>

                {/* Workspace navigation */}
                {filtered.length > 0 && (
                  <p className="mb-1 mt-2 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground/60">
                    Navigasi
                  </p>
                )}
                {filtered.map((ws) => (
                  <button
                    key={ws.slug}
                    onClick={() => handleWorkspaceClick(ws.slug)}
                    className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-left transition-colors hover:bg-secondary"
                  >
                    <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-primary/10 text-xs font-bold text-primary">
                      {ws.name.charAt(0).toUpperCase()}
                    </div>
                    <span className="flex-1">{ws.name}</span>
                    <span className="text-xs text-muted-foreground">/{ws.slug}</span>
                  </button>
                ))}

                {workspaces && filtered.length === 0 && query.trim() && (
                  <p className="px-3 py-4 text-center text-xs text-muted-foreground">
                    Tidak ada workspace cocok dengan "{query}"
                  </p>
                )}
              </>
            )}

            {/* Inline quick task form */}
            {showTaskForm && (
              <div className="px-1 py-1">
                <QuickTaskForm initialTitle={query.trim()} />
              </div>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
